import React, { useState } from 'react';
import { Badge } from 'antd';
import { CopyOutlined, DeleteOutlined, EyeOutlined, EditOutlined, PlusCircleOutlined, SlidersOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { concat, equals, find, not, propEq, propOr, isNotNil, isNotEmpty } from 'ramda';
import { Components } from '@fasstech/flexiget';
import DocumentRules from '../../../rules/DocumentRules';
import SelectWidget from '../../SelectWidget';
import { errorMessage, infoMessage, successMessage } from '../../../../../../utils/messageMutation';
import DuplicateWidgetMutation from '../../../../../../_graphql/mutations/documents/DuplicateWidgetMutation';
import { useDocumentContext } from '../../../../../../context/Document';
import { originFromItemPath } from '../../../../../../lib/sessionHistory';
import colors from '../../../../../../colors';
import ConfirmButton from '../../../../../../components/ConfirmButton';
import useActionGuard from '../../../../../../hooks/useActionGuard';
import { buildCardColId } from '../../helpers/highlightNavigation';

const WidgetForm = ({
  index,
  widgetId,
  itemPathPrefix,
  rowIndex,
  widgetType,
  sectionIndex,
  rules,
  getWidgetVariables,
  getRulesAreValid,
  getConditionsAreValid,
  addWidgetRule,
  removeWidgetRule,
  updateWidgetRule,
  updateWidget,
  removeWidget,
  canRemove,
  type,
  setIsDynamicTableInSection,
  setIsOpenCreateWidgetModal,
  userCanEdit
}) => {
  const { variablesForRules, scope, setWidgetIdEditing, setWidgetEditOrigin, setRowIndexEditing, setWidgetCreated, setDuplicateWidgetPlacement, setIsOpenCreateGlobalVariableModal, widgets: widgetsList } = useDocumentContext();

  const [displayRules, setDisplayRules] = useState(false);
  const widgetHasRules = (isNotNil(widgetId) && isNotNil(rules)) || isNotEmpty(variablesForRules);
  const variables = concat(getWidgetVariables(widgetId), variablesForRules);
  const canShowRules = widgetHasRules && isNotEmpty(rules);
  const { loading, run } = useActionGuard();

  const onDuplicateWidget = () => {
    infoMessage('Duplication du widget en cours...');
    run(`duplicate-widget-${widgetId}`, (done) => DuplicateWidgetMutation({ widgetId }, (ok, error, widgetIdDuplicate, name) => {
      if (ok && !error) {
        successMessage('widget', 'dupliqué');
        const widgetDuplicated = find(propEq(widgetId, 'id'))(widgetsList);
        const { sectionIndex, block } = originFromItemPath(itemPathPrefix);
        setDuplicateWidgetPlacement({
          rowIndex, index, sectionIndex, type: block
        });
        setWidgetCreated({
          ...widgetDuplicated, name, id: widgetIdDuplicate, rootDocumentId: widgetId, dates: { creation: new Date() }
        });
      } else {
        errorMessage();
      }
      done(ok, error, widgetIdDuplicate, name);
    }));
  };

  return (
    <div 
      id={buildCardColId(sectionIndex, widgetType, rowIndex, index)}
      className="w-full pl-1 py-2"
    >
      <div>
        <div className="grid grid-cols-5 gap-4 items-center w-full">
          <SelectWidget
            updateWidget={updateWidget}
            index={index}
            rowIndex={rowIndex}
            widgetId={widgetId}
            scope={scope}
            itemPathPrefix={itemPathPrefix}
            setIsDynamicTableInSection={setIsDynamicTableInSection}
            rules={rules}
            userCanEdit={userCanEdit}
            className="col-span-3 !w-full"
          />
          <div className="col-span-2 grid grid-cols-5 gap-x-6 items-center">
            {(userCanEdit || (!userCanEdit && canShowRules)) && (
              <Badge
                dot={canShowRules}
                color={canShowRules ? colors['flexi-primary-color'] : undefined}
              >
                <Components.Tooltip
                  title={`${userCanEdit ? 'Ajouter' : 'Consulter'} une règle`}
                >
                  <SlidersOutlined
                    onClick={widgetId ? () => setDisplayRules(not) : null}
                    rotate={90}
                    className={classNames({ 'opacity-50': !widgetId })}
                  />
                </Components.Tooltip>
              </Badge>
            )}
            {userCanEdit && (
              <>
                <Components.Tooltip
                  title="Créer un widget"
                >
                  <PlusCircleOutlined
                    className="create-widget-button"
                    onClick={() => {
                      const typeRow = equals('content', type) ? 'widgets' : type;
                      setRowIndexEditing([typeRow, rowIndex]);
                      const { sectionIndex, block } = originFromItemPath(itemPathPrefix);
                      setIsOpenCreateWidgetModal({
                        updateWidget, rowIndex, index, sectionIndex, type: block
                      });
                    }}
                  />
                </Components.Tooltip>
                <Components.Tooltip
                  title="Dupliquer le widget"
                >
                  <CopyOutlined
                    onClick={widgetId ? onDuplicateWidget : null}
                    className={classNames('hover:text-flexi-grey', { 'opacity-50': !widgetId || propOr(false, `duplicate-widget-${widgetId}`, loading) })}
                  />
                </Components.Tooltip>
              </>
            )}

            <Components.Tooltip
              title={userCanEdit ? 'Éditer le widget' : 'Voir le widget'}
            >
              {userCanEdit ? (
                <EditOutlined
                  onClick={widgetId ? () => {
                    setWidgetIdEditing(widgetId);
                    const typeRow = equals('content', type) ? 'widgets' : type;
                    setRowIndexEditing([typeRow, rowIndex]);
                    setWidgetEditOrigin(originFromItemPath(itemPathPrefix));
                  } : null}
                  className={classNames({ 'opacity-50': !widgetId })}
                />
              ) : (
                <EyeOutlined
                  onClick={widgetId ? () => {
                    setWidgetIdEditing(widgetId);
                    const typeRow = equals('content', type) ? 'widgets' : type;
                    setRowIndexEditing([typeRow, rowIndex]);
                    setWidgetEditOrigin(originFromItemPath(itemPathPrefix));
                  } : null}
                  className={classNames({ 'opacity-50': !widgetId })}
                />
              )}
            </Components.Tooltip>

            {userCanEdit && (

              <Components.Tooltip
                title="Supprimer le widget"
              >
                <ConfirmButton
                  title="Êtes-vous sûr de vouloir supprimer ce widget ?"
                  onConfirm={() => {
                    setIsDynamicTableInSection(false);
                    removeWidget(rowIndex, index);
                  }}
                  disabled={!canRemove}
                  icon={<DeleteOutlined className={classNames('btn-remove-widget hover:text-flexi-red', { 'opacity-50 !cursor-default': !canRemove })} />}
                  buttonType="text"
                  sizeButton="small"
                  className="!w-3.5 !h-3.5"
                />
              </Components.Tooltip>
            )}
          </div>
        </div>
        {widgetHasRules && displayRules && (
          <DocumentRules
            variables={variables}
            rules={rules}
            rulesAreValid={getRulesAreValid(rules)}
            getConditionsAreValid={getConditionsAreValid}
            onAdd={addWidgetRule(rowIndex, index)}
            onRemove={removeWidgetRule(rowIndex, index)}
            onUpdate={updateWidgetRule(rowIndex, index)}
            userCanEdit={userCanEdit}
            setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
          />
        )}
      </div>
    </div>
  );
};

export default WidgetForm;
